import api from "../../../utils/api";

// ---------------------------------------------------------------------------
// Vista del cliente desde CC-CONSOLE (CC-CONSOLE #981 / épica #983).
// Todo pasa por el backend de ESTA app (`/ccTenantView/*`): el token de cliente
// vive solo en el servidor y el alcance (solo Wayak) lo decide CC-CONSOLE.
// Las respuestas vienen como `{ meta, data }`: se devuelve `data`.
// ---------------------------------------------------------------------------

const data = <T,>(res: { data?: { data?: T } }): T => res?.data?.data as T;

export interface ClientRef {
  id: string;
  name: string | null;
}

export interface TenantContext {
  client: ClientRef;
  provider: { name: string | null };
  shareTeam: boolean;
  resources: string[];
}

export interface WorkHoursView {
  client: ClientRef;
  from: string;
  to: string;
  shareTeam: boolean;
  estimated: boolean;
  summary: {
    totalHours: number;
    recordedHours?: number;
    estimatedHours?: number;
    activeUsers: number;
    totalCommits: number;
    byUser: { userName: string | null; hours: number; days: number; commits: number }[];
    weekly: { weekStart: string; hours: number }[];
    monthly: { month: string; hours: number }[];
    byProject?: { projectId: string; projectLabel: string; hours: number }[];
    byRepo: { repo: string; hours: number }[];
    pullRequests: { key: string; repo: string; number: number; title: string | null; url: string | null; hours: number }[];
  };
  repos: string[];
  expected: { teamExpectedHours: number; teamActualHours: number; gapHours: number } | null;
}

export interface TeamView {
  client: ClientRef;
  date: string;
  shareTeam: boolean;
  headcount: number;
  weeklyHours: number;
  byRole: Record<string, number>;
  members: {
    name: string | null;
    role: string | null;
    weeklyCapacityHours: number;
    weeklyHours: number;
    allocations: { projectName: string | null; percentage: number; weeklyHours: number; startDate: string | null; endDate: string | null }[];
  }[];
}

export interface ExpensesView {
  client: ClientRef;
  from: string;
  to: string;
  baseCurrency: string;
  total: number;
  months: { period: string; total: number; count: number; withoutRate: number; byCategory: Record<string, number> }[];
  expenses: {
    id: string;
    date: string;
    vendor: string;
    category: string;
    description: string;
    amount: number;
    currency: string;
    amountBase: number | null;
    invoiceStatus: string;
  }[];
}

const get = async <T,>(resource: string, params?: Record<string, string>) =>
  data<T>(await api.get(`/ccTenantView/${resource}`, { params }));

export const fetchContext = () => get<TenantContext>("context");
export const fetchWorkHours = (from: string, to: string) => get<WorkHoursView>("workHours", { from, to });
export const fetchTeam = () => get<TeamView>("team");
export const fetchExpenses = (from: string, to: string) => get<ExpensesView>("expenses", { from, to });

// --- CRM del producto Wayak, leído en vivo de CC-CONSOLE (CC-CONSOLE #1415) ---

export interface CrmStageStep {
  stageId: string;
  label: string | null;
  enteredAt: string | null;
  exitedAt: string | null;
  kind?: string | null;
}

export interface CrmDealView {
  id: string;
  title: string | null;
  company: string | null;
  contact: string | null;
  amount: number;
  amountKind: "mensual" | "proyecto" | null;
  amountNote: string | null;
  quotedMonthly: number | null;
  quotedOneTime: number | null;
  stage: string | null;
  hubspotStage: string | null;
  hubspotStageLabel: string | null;
  hubspotStageOrder: number | null;
  hubspotPipeline: string | null;
  hubspotPipelineLabel: string | null;
  hubspotStageKind: string | null;
  quoteAttachmentId: string | null;
  quoteAttachmentName: string | null;
  stageHistory: CrmStageStep[];
  closeDate: string | null;
  createdAt: string | null;
  product: string | null;
  source: string | null;
}

export interface CrmQuoteView {
  id: string;
  title: string | null;
  amount: number;
  currency: string | null;
  status: string | null;
  dealId: string | null;
  company: string | null;
  contact: string | null;
  dealStageLabel: string | null;
  attachmentId: string | null;
  attachmentName: string | null;
  documentUrl: string | null;
  sentAt: string | null;
  expiresAt: string | null;
}

export interface CrmContactView {
  id: string;
  name: string | null;
  email: string | null;
  whatsapp: string | null;
  company: string | null;
  role: string | null;
  stage: string | null;
  source: string | null;
  createdAt: string | null;
}

export interface CrmFileLink {
  url: string;
  name: string | null;
  contentType: string | null;
  expiresInMs: number;
}

const list = async <T,>(resource: string) => (await get<T[] | null>(resource)) ?? [];
export const fetchCrmDeals = () => list<CrmDealView>("crmDeals");
export const fetchCrmQuotes = () => list<CrmQuoteView>("crmQuotes");
export const fetchCrmContacts = () => list<CrmContactView>("crmContacts");
/** Enlace firmado (caduca en minutos) a un adjunto: se pide al abrirlo, nunca se guarda. */
export const fetchCrmFile = (id: string) => get<CrmFileLink>("crmFile", { id });

/** Código estable del error (`meta.messageKey`) para decir QUÉ pasó. */
export function failureCode(err: unknown): string | null {
  const e = err as { response?: { status?: number; data?: { meta?: { messageKey?: string } } } };
  return e?.response?.data?.meta?.messageKey ?? (e?.response?.status ? `HTTP_${e.response.status}` : null);
}
